import {
  Box,
  Button,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { EventDetailManagedCampaignsSection } from "@common/interfaces/KYDEvent";
import { SectionProps } from "../../../sections/sectionsComponents";
import actions, { Ad, CreateAdsetRequest } from "../../../../../actions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CreateCampaignWizard, {
  Campaign,
  Adset,
  Step,
} from "./CreateCampaignWizard";
import { FaExclamationTriangle, FaPlus } from "react-icons/fa";
import PreviewDialog from "../PreviewDialog";
import { useLoading } from "../../../../hooks/form";
import CampaignList from "./CampaignList";
import { usePolling } from "../usePolling";
import { loadingMessageToText } from "../util";
import transform, { AdCreativeFormValues } from "./transform/adcreative";
import { Link } from "react-router-dom";

const ManagedCampaignsSection = ({
  data,
  refetchEvent,
}: SectionProps<EventDetailManagedCampaignsSection["data"]>) => {
  const [pixels, setPixels] = useState<any[]>([]);
  const [pages, setPages] = useState<{ id: string; name: string }[]>([]);
  const [instagramAccounts, setInstagramAccounts] = useState<
    { id: string; username: string }[]
  >([]);
  const [isExpanded, setIsExpanded] = useState<Record<string, boolean>>({});
  const [audiences, setAudiences] = useState<any[]>([]);
  const [previewHtml, setPreviewHtml] = useState<
    { name: string; html: string }[]
  >([]);
  const previewRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const [loading, setLoading] = useLoading<{
    adsets: boolean;
    campaigns: boolean;
    audiences: boolean;
    pixels: boolean;
    preview: boolean;
    publish: Record<string, boolean>;
    adaccounts: boolean;
    search: boolean;
    instagramAccounts: boolean;
    pages: boolean;
  }>({
    adsets: false,
    campaigns: false,
    audiences: false,
    pixels: false,
    preview: false,
    publish: {},
    adaccounts: false,
    search: false,
    instagramAccounts: false,
    pages: false,
  });
  const [loadingMessage, setLoadingMessage] = useState<Record<
    string,
    string
  > | null>({});

  const [step, _setStep] = useState<Step>("none");
  const [steps, _setSteps] = useState<Step[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const setStep = useCallback(
    (step: Step) => {
      _setStep(step);

      if (step === "none") {
        _setSteps([]);
        return;
      }

      _setSteps((steps) => steps.concat(step));
    },
    [_setStep, _setSteps]
  );

  const [defaultCampaign, setDefaultCampaign] = useState<Campaign | null>(null);
  const [defaultEditingAdset, setDefaultEditingAdset] = useState<Adset | null>(
    null
  );
  const [defaultEditingAd, setDefaultEditingAd] = useState<Ad | null>(null);

  const fetchCampaign = useCallback(() => {
    setLoading("campaigns", true);
    return refetchEvent({
      event_id: data.id,
    }).finally(() => {
      setLoading("campaigns", false);
    });
  }, [setLoading]);

  const createCampaign = useCallback(
    async (campaignData: {
      id?: string;
      name: string;
      budget: string;
      campaign_type: "conversion" | "awareness";
      start_time: string;
      stop_time: string;
      page_id: string;
      ad_account: { value: string; label: string };
    }) => {
      setLoading("campaigns", true);
      setLoading("publish", {
        ...loading.publish,
        [campaignData.id]: true,
      });

      const method = campaignData.id
        ? actions.ADMIN.updateCampaign
        : actions.ADMIN.createCampaign;

      return method({
        id: campaignData.id,
        ad_account_id: campaignData.ad_account.value,
        event_id: data.id,
        name: campaignData.name,
        campaign_type: campaignData.campaign_type,
        start_time: campaignData.start_time,
        stop_time: campaignData.stop_time,
        page_id: campaignData.page_id,
      })
        .then(async (r) => {
          if (r.update_id) {
            pollForResult({
              event_id: data.id,
              campaign_id: campaignData.id,
              update_id: r.update_id,
              title: "Campaign published",
              description: "Campaign published",
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaignData.id]: false,
            });
          }

          await refetchRelated({ ad_account_id: r.campaign.ad_account_id });
          await fetchCampaign();

          setDefaultCampaign(r.campaign);

          return r;
        })
        .catch((r) => {
          toast({
            title: "Error publishing campaign",
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaignData.id]: false,
          });
        })
        .finally(() => {
          setLoading("campaigns", false);
        });
    },
    [fetchCampaign]
  );

  const previewAd = useCallback(
    ({ adset_id, campaign_id }: { adset_id: string; campaign_id: string }) => {
      setLoading("preview", true);
      setIsPreviewOpen(true);
      return actions.ADMIN.previewAd({
        adset_id,
        campaign_id,
        event_id: data.id,
      })
        .then((r) => {
          // console.log(r);
          setPreviewHtml(
            r.previews.map((p) => ({
              name: p.name,
              html: p.preview.body,
            }))
          );
        })
        .finally(() => {
          setLoading("preview", false);
        });
    },
    [data]
  );

  const checkCampaign = useCallback(
    async (event_id: string, campaign_id: string, execution_id: string) => {
      const campaign = await actions.ADMIN.checkCampaignUpdate(
        event_id,
        campaign_id,
        execution_id
      );
      return campaign;
    },
    [data, defaultCampaign]
  );

  const { start: startCampaignPolling, result: campaignResult } =
    usePolling<any>({
      maxRuns: 10,
      waitFor: 5000,
      checkResult: (result) => result.status !== "PROCESSING",
      poll: async (args: {
        event_id: string;
        campaign_id: string;
        update_id: string;
      }) => {
        const campaign = await checkCampaign(
          args.event_id,
          args.campaign_id,
          args.update_id
        );

        if (campaign.status !== "PROCESSING") {
          setLoadingMessage({});
        } else {
          setLoadingMessage((loadingMessage) => ({
            ...loadingMessage,
            [args.campaign_id]: campaign.update_status,
          }));
        }

        return campaign;
      },
    });

  const pollForResult = useCallback(
    async ({
      event_id,
      campaign_id,
      update_id,
      title,
      description,
    }: {
      event_id: string;
      campaign_id: string;
      update_id: string;
      title?: string;
      description?: string;
    }) => {
      const result = await startCampaignPolling({
        event_id,
        campaign_id,
        update_id,
      });

      console.log(`Result of polling`, result);

      if (result && !result.error) {
        toast({
          title: title || "Campaign published",
          description: description || "Campaign published",
          status: "success",
        });
      } else {
        toast({
          title: "Error publishing campaign",
          description: result.error,
          status: "error",
        });
      }

      await refetchEvent({
        event_id: data.id,
      });
      setLoading("publish", {
        ...loading.publish,
        [campaign_id]: false,
      });
    },
    [startCampaignPolling, toast, setLoading, refetchEvent, loading]
  );

  const publishCampaign = useCallback(
    (campaign: { id: string }, extra?: { publish_all_adsets?: boolean }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign.id]: true,
      });
      return actions.ADMIN.publishCampaign({
        event_id: data.id,
        campaign_id: campaign.id,
        publish_all_adsets: extra?.publish_all_adsets,
      })
        .then((r) => {
          refetchEvent({
            event_id: data.id,
          });

          if (r.update_id) {
            pollForResult({
              event_id: data.id,
              campaign_id: campaign.id,
              update_id: r.update_id,
              title: "Campaign published",
              description: "Campaign published",
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaign.id]: false,
            });
          }

          refetchEvent({
            event_id: data.id,
          });
        })
        .catch((r) => {
          toast({
            title: "Error publishing campaign",
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaign.id]: false,
          });
        });
    },
    [data, refetchEvent, pollForResult, loading]
  );

  const unpublishCampaign = useCallback(
    (campaign: { id: string; name: string }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign.id]: true,
      });

      refetchEvent({
        event_id: data.id,
      });

      return actions.ADMIN.unpublishCampaign({
        event_id: data.id,
        campaign_id: campaign.id,
      })
        .then((r) => {
          refetchEvent({
            event_id: data.id,
          });

          if (r.update_id) {
            pollForResult({
              event_id: data.id,
              campaign_id: campaign.id,
              update_id: r.update_id,
              title: `Campaign "${campaign.name}" unpublished from Meta`,
              description: `Campaign "${campaign.name}" unpublished from Meta`,
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaign.id]: false,
            });
          }

          refetchEvent({
            event_id: data.id,
          });
        })
        .catch((r) => {
          toast({
            title: `Error unpublishing campaign "${campaign.name}"`,
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaign.id]: false,
          });
        });
    },
    [data, refetchEvent, pollForResult, loading]
  );

  const unpublishAdset = useCallback(
    ({
      adset_id,
      campaign_id,
      event_id,
    }: {
      adset_id: string;
      campaign_id: string;
      event_id: string;
    }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign_id]: true,
      });

      const adset = data.campaigns
        .find((c) => c.id === campaign_id)
        ?.adsets.find((a) => a.id === adset_id);
      return actions.ADMIN.unpublishAdset({
        event_id,
        campaign_id,
        adset_id,
      })
        .then((r) => {
          refetchEvent({
            event_id: data.id,
          });

          if (r.update_id) {
            pollForResult({
              event_id,
              campaign_id,
              title: `Adset "${adset.name}" unpublished from Meta`,
              description: `Adset "${adset.name}" unpublished from Meta`,
              update_id: r.update_id,
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaign_id]: false,
            });
          }

          refetchEvent({
            event_id: data.id,
          });
        })
        .catch((r) => {
          toast({
            title: "Error unpublishing adset",
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaign_id]: false,
          });
        });
    },
    [refetchEvent, pollForResult, loading]
  );

  const publishAdset = useCallback(
    ({
      adset_id,
      campaign_id,
      event_id,
    }: {
      adset_id: string;
      campaign_id: string;
      event_id: string;
    }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign_id]: true,
      });

      const adset = data.campaigns
        .find((c) => c.id === campaign_id)
        ?.adsets.find((a) => a.id === adset_id);

      return actions.ADMIN.publishAdset({
        event_id,
        adset_id,
        campaign_id,
      })
        .then((r) => {
          refetchEvent({
            event_id: data.id,
          });

          if (r.update_id) {
            pollForResult({
              event_id,
              campaign_id,
              update_id: r.update_id,
              title: `Adset "${adset.name}" published to Meta`,
              description: `Adset "${adset.name}" published to Meta`,
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaign_id]: false,
            });
          }

          const campaign = data.campaigns.find(
            (c) => c.id === campaign_id
          ) as Campaign;
        })
        .catch((r) => {
          toast({
            title: "Error publishing adset",
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaign_id]: false,
          });
        });
    },
    [refetchEvent, pollForResult, loading]
  );

  const createAd = useCallback(
    (adData: CreateAdsetRequest) => {
      setLoading("adsets", true);
      if (adData.external_id) {
        setLoading("publish", {
          ...loading.publish,
          [adData.campaign_id]: true,
        });
      }

      const method = adData.id
        ? actions.ADMIN.updateAdset
        : actions.ADMIN.createAdset;

      return method({
        id: adData.id,
        audience_ids: adData.audience_ids,
        campaign_id: adData.campaign_id,
        event_id: data.id,
        name: adData.name,
        lifetime_budget: adData.lifetime_budget,
        custom_audience_files: adData.custom_audience_files,
        pixel_id: adData.pixel_id,
        start_time: adData.start_time,
        stop_time: adData.stop_time,
        age_min: adData.age_min,
        age_max: adData.age_max,
        campaign_type: adData.campaign_type,
        caption: adData.caption,
        optimization_goal: adData.optimization_goal,
        countries: adData.countries,
        interests: adData.interests,
        behaviors: adData.behaviors,
        demographics: adData.demographics,
        geo_markets: adData.geo_markets,
      })
        .then(async (r) => {
          setLoading("adsets", false);

          if (r.update_id) {
            pollForResult({
              event_id: data.id,
              campaign_id: adData.campaign_id,
              update_id: r.update_id,
              title: "Adset published to Meta",
              description: "Adset published to Meta",
            });
          } else {
            toast({
              title: adData.id ? "Adset updated" : "Adset created",
              description: `Adset ${adData.name} updated`,
              status: "success",
            });
            setLoading("publish", {
              ...loading.publish,
              [adData.campaign_id]: false,
            });
          }
          refetchEvent({
            event_id: data.id,
          });

          setDefaultEditingAdset(r.adset);
          setDefaultCampaign(r.campaign);

          return r;
        })
        .catch((r) => {
          toast({
            title: "Error updating adset",
            description: r.message,
            status: "error",
          });

          setLoading("adsets", false);
          setLoading("publish", {
            ...loading.publish,
            [adData.campaign_id]: false,
          });
        });
    },
    [pollForResult, loading, refetchEvent]
  );

  const createAdCreative = useCallback(
    (adData: AdCreativeFormValues) => {
      setLoading("adsets", true);
      if (adData.status === "ACTIVE") {
        setLoading("publish", {
          ...loading.publish,
          [adData.campaign_id]: true,
        });
      }

      const method = actions.ADMIN.updateAdCreative;
      const otherAds = defaultEditingAdset?.ads.filter(
        (ad) => ad.id !== adData.id
      );

      return method({
        ads: otherAds
          .map((ad) => {
            return transform.output({
              data: transform.input({
                ad,
                campaign: defaultCampaign,
                adset: defaultEditingAdset,
                pages: pages,
                instagramAccounts: instagramAccounts,
              }),
              adset: defaultEditingAdset,
            });
          })
          .concat(
            transform.output({
              data: adData,
              adset: defaultEditingAdset,
            })
          ),
        event_id: data.id,
        campaign_id: adData.campaign_id,
        adset_id: adData.adset_id,
        organization_id: adData.organization_id,
      })
        .then(async (r) => {
          setLoading("adsets", false);

          if (r.update_id) {
            pollForResult({
              event_id: data.id,
              campaign_id: adData.campaign_id,
              update_id: r.update_id,
              title: "Adcreative published to Meta",
              description: "Adcreative published to Meta",
            });
          } else {
            toast({
              title: "Creative updated",
              description: `Creative updated`,
              status: "success",
            });
            setLoading("publish", {
              ...loading.publish,
              [adData.campaign_id]: false,
            });
            setLoading("adsets", false);
          }
          refetchEvent({
            event_id: data.id,
          });

          setDefaultCampaign(r.campaign);
          setDefaultEditingAdset(r.adset);
          setDefaultEditingAd(
            r.adset.ads.find(
              (newAd) => !otherAds.map((ad) => ad.id).includes(newAd.id)
            ) || null
          );

          // return r;
        })
        .catch((r) => {
          console.log("r", r);
          toast({
            title: "Error updating adcreative",
            description: r.message,
            status: "error",
          });

          setLoading("adsets", false);
          setLoading("publish", {
            ...loading.publish,
            [adData.campaign_id]: false,
          });
        });
    },
    [
      setDefaultEditingAdset,
      data,
      defaultCampaign,
      defaultEditingAdset,
      setDefaultCampaign,
      pages,
      instagramAccounts,
      pollForResult,
      loading,
      refetchEvent,
    ]
  );

  const searchInterests = useCallback(
    (query: string) => {
      setLoading("search", true);
      return actions.ADMIN.searchInterests({
        event_id: data.id,
        query,
      }).finally(() => {
        setLoading("search", false);
      });
    },
    [data]
  );

  const searchGeoMarkets = useCallback(
    (query: string) => {
      setLoading("search", true);
      return actions.ADMIN.searchGeoMarkets({
        event_id: data.id,
        query,
      }).finally(() => {
        setLoading("search", false);
      });
    },
    [data]
  );

  const listPixels = useCallback(
    ({ ad_account_id }: { ad_account_id: string }) => {
      setLoading("pixels", true);
      return actions.ADMIN.listPixels(data.id, {
        ad_account_id: ad_account_id,
      })
        .then((res) => {
          setPixels(res);
        })
        .finally(() => {
          setLoading("pixels", false);
        });
    },
    [defaultCampaign, data]
  );

  const listInstagramAccounts = useCallback(
    ({ ad_account_id }: { ad_account_id: string }) => {
      setLoading("instagramAccounts", true);
      return actions.ADMIN.listInstagramAccounts(data.id, {
        ad_account_id,
      })
        .then((res) => {
          setInstagramAccounts(res);
        })
        .finally(() => {
          setLoading("instagramAccounts", false);
        });
    },
    [defaultCampaign, data]
  );

  const listPages = useCallback(
    ({ ad_account_id }: { ad_account_id: string }) => {
      setLoading("pages", true);
      return actions.ADMIN.listPages(data.id, {
        ad_account_id,
      })
        .then((res) => {
          setPages(res);
        })
        .finally(() => {
          setLoading("pages", false);
        });
    },
    [defaultCampaign, data]
  );

  const refetchRelated = useCallback(
    async ({ ad_account_id }: { ad_account_id?: string }) => {
      if (!ad_account_id) {
        return;
      }

      await Promise.all([
        listPages({ ad_account_id }),
        listPixels({ ad_account_id }),
        listInstagramAccounts({ ad_account_id }),
      ]).catch((e) => {
        toast({
          title: "Error fetching related data",
          description: e.message,
          status: "error",
          isClosable: true,
        });
      });
    },
    [fetchCampaign, listPages, listPixels]
  );

  useEffect(() => {
    if (step !== "none" && defaultCampaign && defaultCampaign.ad_account_id) {
      refetchRelated({ ad_account_id: defaultCampaign.ad_account_id });
    }
  }, [step, defaultCampaign, refetchRelated]);

  return (
    <Stack gap={8} overflow="scroll" w="full">
      <VStack spacing={4}>
        <Stack
          direction={{ base: "column", md: "row" }}
          justify={"space-between"}
          alignItems={"center"}
          w="100%"
        >
          <Text fontWeight={"bold"}>Managed Campaigns</Text>
          <HStack>
            <IconButton
              variant={"outline"}
              aria-label="Add campaign"
              isDisabled={!data.meta.is_logged_in}
              icon={<FaPlus />}
              onClick={() => {
                setStep("create_campaign");
                setDefaultCampaign(null);
                setDefaultEditingAdset(null);
                setDefaultEditingAd(null);
              }}
            />
          </HStack>
        </Stack>
        <CreateCampaignWizard
          step={step}
          setStep={setStep}
          steps={steps}
          refetchAdsets={() => {
            void fetchCampaign();
            return Promise.resolve();
          }}
          defaultCampaign={defaultCampaign}
          close={() => {
            setDefaultCampaign(null);
            setDefaultEditingAdset(null);
            setDefaultEditingAd(null);
            setStep("none");
          }}
          defaultAd={defaultEditingAd}
          setDefaultEditingAd={setDefaultEditingAd}
          setDefaultEditingAdset={setDefaultEditingAdset}
          onPublish={publishCampaign}
          onCreateAd={createAd}
          onCreateAdCreative={createAdCreative}
          onCreateCampaign={createCampaign}
          loading={{
            ...loading,
            publish: loading.publish[defaultCampaign?.id || ""],
          }}
          currency={data.currency}
          audiences={audiences}
          campaigns={data.campaigns}
          defaultEditingAdset={defaultEditingAdset}
          adAccounts={data.accounts}
          pixels={pixels}
          pages={pages}
          instagramAccounts={instagramAccounts}
          search={{
            interests: searchInterests,
            geo_markets: searchGeoMarkets,
          }}
          createUploadUrl={actions.ADMIN.createUploadUrl}
        />
        <PreviewDialog
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewHtml([]);
          }}
          title="Preview"
          isLoading={loading.preview}
        >
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={8}
            alignItems={"center"}
            justifyContent={"center"}
            w="full"
          >
            {previewHtml.map((p) => (
              <GridItem key={p.name} overflow={"hidden"}>
                <VStack
                  key={p.name}
                  height={"full"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <Text>{p.name}</Text>
                  <Box
                    alignItems={"center"}
                    background={"gray.200"}
                    h="full"
                    minH={"500px"}
                    height={"500px"}
                    ref={previewRef}
                    dangerouslySetInnerHTML={{ __html: p.html }}
                  />
                </VStack>
              </GridItem>
            ))}
          </Grid>
        </PreviewDialog>
      </VStack>
      {!data.meta.is_logged_in ? (
        <HStack
          w="full"
          p={4}
          mt={4}
          bgColor="#fafafa"
          borderRadius={"md"}
          justifyContent={"center"}
        >
          <VStack w="full" alignItems={"flex-start"}>
            <HStack>
              <Icon as={FaExclamationTriangle} />
              <Text fontWeight={"bold"}>Account not connected to Meta</Text>
            </HStack>
            <Text>
              Please connect to your Meta ads account to manage your campaigns
              through the KYD dashboard.
            </Text>
            {data.campaigns.length > 0 ? (
              <Text fontSize={"xs"}>
                Any changes made to the campaigns won't be propagated to Meta.
              </Text>
            ) : null}
          </VStack>
          <Button
            flexShrink={0}
            as={Link}
            colorScheme={"gray"}
            px={2}
            to="/settings"
          >
            Go to settings
          </Button>
        </HStack>
      ) : null}
      <CampaignList
        onAddAdset={(campaign) => {
          setStep("edit_adset");
          setDefaultCampaign(campaign);
        }}
        isLoggedIn={data.meta.is_logged_in}
        loadingMessage={(campaign_id) =>
          loadingMessageToText(loadingMessage[campaign_id])
        }
        onPublishCampaign={publishCampaign}
        onUnpublishCampaign={unpublishCampaign}
        campaigns={data.campaigns || []}
        publishAdset={publishAdset}
        unpublishAdset={unpublishAdset}
        previewAd={previewAd}
        setIsExpanded={setIsExpanded}
        isExpanded={isExpanded}
        loading={loading}
        setDefaultCampaign={setDefaultCampaign}
        setStep={setStep}
        setDefaultEditingAdset={setDefaultEditingAdset}
      />
    </Stack>
  );
};

export default ManagedCampaignsSection;
